import { C } from '../data/constants.js';

export const Physics = {
  // Test ball vs paddle collision. Returns true if collision occurred.
  // If collision: modifies ball.vx, ball.vy, ball.x, ball.y in place.
  ballPaddle(ball, paddle) {
    if (ball.attached || ball.vy <= 0) return false;
    
    // Simple AABB check first
    const bLeft = ball.x - ball.radius;
    const bRight = ball.x + ball.radius;
    const bTop = ball.y - ball.radius;
    const bBottom = ball.y + ball.radius;
    
    if (bRight < paddle.left || bLeft > paddle.right) return false;
    if (bBottom < paddle.top || bTop > paddle.bottom) return false;
    
    // Collision! Resolve position
    ball.y = paddle.top - ball.radius;
    
    // Calculate reflection angle based on where the ball hit the paddle
    // hitPos: -1 (left edge) to +1 (right edge)
    const hitPos = (ball.x - paddle.x) / (paddle.w / 2);
    const clampedHit = Math.max(-1, Math.min(1, hitPos));
    
    // Reflection angle: center = straight up, edges = steep angle
    // Range: -70° to -110° (where -90° is straight up)
    const maxAngle = 70 * Math.PI / 180;
    const angle = -Math.PI / 2 + clampedHit * maxAngle;
    
    // Speed varies by hit position: center = 1.3x, edges = 0.7x
    const speedFactor = 1.3 - Math.abs(clampedHit) * 0.6;
    const speed = ball.speed * speedFactor;
    
    ball.vx = Math.cos(angle) * speed;
    ball.vy = Math.sin(angle) * speed;
    
    // Apply spin from paddle velocity
    const spin = paddle.velocity * C.SPIN_COEFFICIENT;
    ball.vx += spin;
    
    return true;
  },

  // Test ball vs single brick collision.
  // Returns: { hit: false } or { hit: true, destroyed: boolean, brick: brick, pierced: boolean }
  ballBrick(ball, brick, isFireball = false) {
    if (!brick.collidable) return { hit: false };
    
    // Find closest point on brick AABB to ball center
    const closestX = Math.max(brick.x, Math.min(ball.x, brick.x + brick.w));
    const closestY = Math.max(brick.y, Math.min(ball.y, brick.y + brick.h));
    
    const dx = ball.x - closestX;
    const dy = ball.y - closestY;
    const distSq = dx * dx + dy * dy;
    
    if (distSq > ball.radius * ball.radius) return { hit: false };
    
    // Collision detected!
    const dist = Math.sqrt(distSq) || 0.001;

    // FIREBALL MODE: incinerates and pierces straight through destructible bricks
    if (isFireball) {
      if (brick.type === C.BRICK_INDESTRUCTIBLE) {
        // Bounce off indestructible barriers
        const overlap = ball.radius - dist;
        ball.x += (dx / dist) * overlap;
        ball.y += (dy / dist) * overlap;
        
        const overlapLeft = ball.x + ball.radius - brick.x;
        const overlapRight = (brick.x + brick.w) - (ball.x - ball.radius);
        const overlapTop = ball.y + ball.radius - brick.y;
        const overlapBottom = (brick.y + brick.h) - (ball.y - ball.radius);
        const minOverlap = Math.min(overlapLeft, overlapRight, overlapTop, overlapBottom);
        
        if (minOverlap === overlapLeft || minOverlap === overlapRight) {
          ball.vx = -ball.vx;
        } else {
          ball.vy = -ball.vy;
        }
        return { hit: true, destroyed: false, brick, pierced: false };
      }

      // Incinerate destructible brick with massive damage & pierce straight through
      const destroyed = brick.hit(99);
      return { hit: true, destroyed, brick, pierced: true };
    }
    
    // NORMAL BALL MODE: Push ball out of brick and bounce
    const overlap = ball.radius - dist;
    ball.x += (dx / dist) * overlap;
    ball.y += (dy / dist) * overlap;
    
    const overlapLeft = ball.x + ball.radius - brick.x;
    const overlapRight = (brick.x + brick.w) - (ball.x - ball.radius);
    const overlapTop = ball.y + ball.radius - brick.y;
    const overlapBottom = (brick.y + brick.h) - (ball.y - ball.radius);
    
    const minOverlap = Math.min(overlapLeft, overlapRight, overlapTop, overlapBottom);
    
    if (minOverlap === overlapLeft || minOverlap === overlapRight) {
      ball.vx = -ball.vx;
    } else {
      ball.vy = -ball.vy;
    }
    
    // Damage the brick by 1 HP
    const destroyed = brick.hit(1);
    
    return { hit: true, destroyed, brick, pierced: false };
  },

  // Check all bricks. Returns array of collision results.
  ballBricks(ball, bricks, isFireball = false) {
    const results = [];
    for (const brick of bricks) {
      const result = this.ballBrick(ball, brick, isFireball);
      if (result.hit) {
        results.push(result);
        if (!isFireball || !result.pierced) {
          break; // Stop on first hit for normal balls or non-pierced collisions
        }
      }
    }
    return results;
  }
};
